import { RADEnvironmentProxy } from 'radpresentation.htmlclient.defaultviewextensions';

import { IAgeDistributionData } from '@/Components/AgeChart/ONVAgeBarChartElement';

// PDO-Typ und Operationsnamen der serverseitigen Statistik-Endpunkte.
const NEWCOMER_PD_TYPE                 = 'ONV.ONV.Newcomer';
const OP_AGE_DISTRIBUTION              = 'GetAgeDistribution';
const OP_AGE_DISTRIBUTION_FOR_LV       = 'GetAgeDistributionForLandesverband';
const OP_AGE_DISTRIBUTION_FOR_LVS      = 'GetAgeDistributionForLandesverbaende';

/**
 * Ruft die Altersverteilung der Newcomer fuer die angegebenen Zeithorizonte ab.
 *
 * Ohne <paramref name="landesverbandPdoId"/> wird der Standard-Endpunkt verwendet,
 * der dem Principal-Filter folgt (jeder Referent sieht nur seinen eigenen
 * Landesverband). Mit gesetzter Id wird gezielt ein Landesverband abgefragt
 * (serverseitig zusaetzlich UND-verknuepft mit dem Principal-Filter, d. h. eine
 * fremde Id liefert ein leeres Ergebnis).
 *
 * Das Parsen der Backend-Antwort liegt bewusst hier, damit beide Views
 * (Startseite und Statistik je Landesverband) denselben Daten-Kontrakt nutzen.
 */
export function fetchAgeDistribution(
    horizons: number[],
    landesverbandPdoId?: string,
): JQueryPromise<IAgeDistributionData | null> {
    const yearsCsv = horizons.slice().sort((a, b) => a - b).join(',');

    const operationName = landesverbandPdoId != null
        ? OP_AGE_DISTRIBUTION_FOR_LV
        : OP_AGE_DISTRIBUTION;

    const operationParameters = landesverbandPdoId != null
        ? [yearsCsv, landesverbandPdoId]
        : [yearsCsv];

    return RADEnvironmentProxy.getInstance().callStaticOperation({
        pdTypeName:          NEWCOMER_PD_TYPE,
        operationName,
        operationParameters,
        contextParameters:   {},
    }).then((result) => parseAgeDistribution(result?.returnValue));
}

/**
 * Ruft die Altersverteilung fuer MEHRERE Landesverbaende in einem Aufruf ab. Das Backend
 * liefert je Landesverband und je Zeithorizont eine Serie (Kreuzprodukt), jeweils mit
 * gesetzter <c>landesverbandId</c>/<c>landesverbandName</c> zur Beschriftung. So koennen
 * mehrere Landesverbaende in einer gemeinsamen Grafik verglichen werden.
 *
 * Hinweis: Bei leerer Auswahl liefert das Backend ein leeres Serien-Array; der Aufrufer
 * (ViewModel) ueberspringt den Aufruf in diesem Fall jedoch ohnehin.
 */
export function fetchAgeDistributionForLandesverbaende(
    horizons: number[],
    landesverbandPdoIds: string[],
): JQueryPromise<IAgeDistributionData | null> {
    const yearsCsv = horizons.slice().sort((a, b) => a - b).join(',');
    const idsCsv   = (landesverbandPdoIds ?? []).join(',');

    return RADEnvironmentProxy.getInstance().callStaticOperation({
        pdTypeName:          NEWCOMER_PD_TYPE,
        operationName:       OP_AGE_DISTRIBUTION_FOR_LVS,
        operationParameters: [yearsCsv, idsCsv],
        contextParameters:   {},
    }).then((result) => parseAgeDistribution(result?.returnValue));
}

/**
 * Normalisiert den Rueckgabewert der Operation. <c>returnValue</c> ist je nach
 * Serialisierung bereits ein Objekt oder ein JSON-String.
 */
export function parseAgeDistribution(returnValue: unknown): IAgeDistributionData | null {
    let raw: unknown = returnValue;

    if (typeof raw === 'string') {
        try { raw = JSON.parse(raw); }
        catch { return null; }
    }

    if (raw == null || typeof raw !== 'object') return null;
    const obj = raw as IAgeDistributionData;
    if (!Array.isArray(obj.series)) return null;
    return obj;
}
